import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  X,
  Home,
  Smartphone,
  PawPrint,
  Sparkles,
  Navigation,
  RefreshCw,
  Ban,
  CreditCard,
  Pencil,
  AlertTriangle,
  MessageCircle,
  Phone,
  Receipt,
} from 'lucide-react';
import { StatusBadge } from '@/components/common/StatusBadge';
import { getAvatarColor, getUserInitials } from '@/components/common/avatarHelpers';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';
import { formatDateTime } from '@/utils/formatters';
import { linkLigar, linkWhatsApp } from '@/utils/telefone';
import { roleLabels, roleClasses, ASSINATURA_LABEL, STATUS_COBRE, daysSince } from './userDisplay';
import { UserDevicesSection } from './UserDevicesSection';
import { CobrancaRecorrenteSection } from './CobrancaRecorrenteSection';
import type { User, UserActivityItem, UserActivityType } from '@/types/user.types';

interface UserDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  user: User | undefined;
  activity: UserActivityItem[] | undefined;
  activityLoading?: boolean;
  funcionariosCount: number | null;
  onEdit: () => void;
  onManageSubscription: () => void;
  onBlock: () => void;
}

// "28/06, 09:41" — data curta pra a timeline de atividade.
function shortDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}, ${p(d.getHours())}:${p(d.getMinutes())}`;
}

// "13/07" — dia/mês (sem ano) pro "até DD/MM" da assinatura.
function dayMonth(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}`;
}

// Dot + ícone da timeline por tipo de evento.
const ACTIVITY_META: Record<
  UserActivityType,
  { dot: string; icon: typeof Smartphone; iconBg: string }
> = {
  login: { dot: 'bg-success', icon: Smartphone, iconBg: 'bg-success/10 text-success' },
  animal: { dot: 'bg-info', icon: PawPrint, iconBg: 'bg-info/10 text-info' },
  assinatura: { dot: 'bg-warning', icon: Sparkles, iconBg: 'bg-warning/10 text-warning' },
  navegacao: { dot: 'bg-tertiary', icon: Navigation, iconBg: 'bg-tertiary/10 text-tertiary' },
};

// Rótulo da assinatura no contexto: "Trial Básico · até 13/07" (própria) ou
// "Coberto · Profissional" (herdada do dono da propriedade) ou "—".
function assinaturaLabel(user: User): { text: string; tone: string } {
  if (user.isProducer && user.assinatura) {
    const status = ASSINATURA_LABEL[user.assinatura.status] ?? user.assinatura.status;
    const plano = user.assinatura.plano?.nome ? ` ${user.assinatura.plano.nome}` : '';
    const ate = user.assinatura.currentPeriodEnd
      ? ` · até ${dayMonth(user.assinatura.currentPeriodEnd)}`
      : '';
    return { text: `${status}${plano}${ate}`, tone: 'text-info' };
  }
  if (user.coberturaHerdada && STATUS_COBRE.has(user.coberturaHerdada.status)) {
    const plano = user.coberturaHerdada.plano?.nome ? ` · ${user.coberturaHerdada.plano.nome}` : '';
    return { text: `Coberto${plano}`, tone: 'text-info' };
  }
  return { text: '—', tone: 'text-text-hint' };
}

export function UserDetailDrawer({
  isOpen,
  onClose,
  user,
  activity,
  activityLoading = false,
  funcionariosCount,
  onEdit,
  onManageSubscription,
  onBlock,
}: UserDetailDrawerProps) {
  const navigate = useNavigate();
  const { can } = useAuth();
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !user) return null;

  const prop = typeof user.property === 'object' ? user.property : null;
  const areas = prop
    ? [
        { label: 'Pastagem', ha: prop.pastagem_ha || 0, bar: 'bg-tertiary', dot: 'bg-tertiary' },
        { label: 'Reserva legal', ha: prop.reserva_legal || 0, bar: 'bg-info', dot: 'bg-info' },
        { label: 'ILPF', ha: prop.sistema_ilpf || 0, bar: 'bg-warning', dot: 'bg-warning' },
        { label: 'Agrícola', ha: prop.area_agricola || 0, bar: 'bg-primary', dot: 'bg-primary' },
      ]
    : [];
  const areasSoma = areas.reduce((acc, a) => acc + a.ha, 0);
  const assinatura = assinaturaLabel(user);
  const isBlocked = user.status === 'blocked' || user.status === 'suspended';
  const devices = user.devices ?? [];
  const currentDevice = devices[0];
  // Vínculos além da propriedade principal (já mostrada no card acima).
  const otherProperties = (user.properties ?? []).filter((p) => p.id !== prop?.id);
  const whatsapp = linkWhatsApp(user.phone);
  const ligar = linkLigar(user.phone);

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-stretch justify-end"
      role="dialog"
      aria-modal="true"
      aria-label="Detalhes do Usuário"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm dark:bg-black/70"
        onClick={onClose}
      />

      <div className="relative z-10 flex h-full w-full max-w-4xl animate-slide-in-right flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] border-l border-border bg-bg-elevated shadow-2xl ring-1 ring-black/5 dark:ring-white/10">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3 lg:px-6 lg:py-4">
          <h2 className="text-lg font-semibold text-text-title lg:text-xl">Detalhes do Usuário</h2>
          <button
            onClick={onClose}
            aria-label="Fechar"
            className="flex size-10 items-center justify-center rounded-lg text-text-secondary transition-colors hover:bg-muted hover:text-text-title lg:size-auto lg:p-1"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body — 2 colunas no desktop; no celular e no tablet uma coluna só,
            que rola inteira (as duas lado a lado espremiam tudo). */}
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto lg:flex-row lg:overflow-hidden">
          {/* Esquerda — contexto */}
          <aside className="flex w-full shrink-0 flex-col border-b border-border bg-muted/30 p-4 lg:max-w-[300px] lg:overflow-y-auto lg:border-b-0 lg:border-r lg:p-6">
            <div className="flex flex-col items-center gap-3 text-center">
              <div
                className={cn(
                  'flex size-16 shrink-0 items-center justify-center rounded-full text-base font-bold tracking-tight',
                  getAvatarColor(user.id)
                )}
              >
                {getUserInitials(user.name)}
              </div>
              <div>
                <p className="font-semibold text-text-title">{user.name}</p>
                <p className="text-sm text-text-secondary">{user.phone}</p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <StatusBadge status={user.status} />
                <span
                  className={cn(
                    'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium',
                    roleClasses[user.role] || 'bg-muted text-text-secondary'
                  )}
                >
                  {roleLabels[user.role] || user.cargo || user.role}
                </span>
                {user.verified && (
                  <span className="inline-flex items-center rounded-full bg-success/10 px-2.5 py-1 text-xs font-medium text-success">
                    Verificado
                  </span>
                )}
              </div>
              {/* Contato direto: abre a conversa no WhatsApp (app no celular,
                  WhatsApp Web no computador) ou a ligação. */}
              {(whatsapp || ligar) && (
                <div className="grid w-full grid-cols-2 gap-2 pt-1">
                  {whatsapp && (
                    <a
                      href={whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex h-11 items-center justify-center gap-2 rounded-lg bg-success/15 text-sm font-semibold text-success transition-colors hover:bg-success/25"
                    >
                      <MessageCircle size={17} />
                      WhatsApp
                    </a>
                  )}
                  {ligar && (
                    <a
                      href={ligar}
                      className="flex h-11 items-center justify-center gap-2 rounded-lg border border-border bg-card text-sm font-semibold text-text-title transition-colors hover:bg-muted"
                    >
                      <Phone size={16} />
                      Ligar
                    </a>
                  )}
                </div>
              )}
              {/* Gera PIX, boleto ou link de cartão já ligado a este produtor. */}
              {user.isProducer && can('finance') && (
                <button
                  type="button"
                  onClick={() =>
                    navigate('/cobrancas', {
                      state: {
                        cobrarProdutor: { id: user.id, name: user.name, phone: user.phone },
                      },
                    })
                  }
                  className="flex h-11 w-full items-center justify-center gap-2 rounded-lg border border-border bg-card text-sm font-semibold text-text-title transition-colors hover:bg-muted"
                >
                  <Receipt size={16} />
                  Cobrar
                </button>
              )}
            </div>

            <div className="my-4 border-t border-border lg:my-5" />

            {/* No celular os dados ficam em duas colunas; os longos ocupam a linha. */}
            <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm lg:flex lg:flex-col lg:gap-4">
              <div className="col-span-2">
                <dt className="mb-1 font-medium text-text-secondary">Último acesso</dt>
                <dd className="text-text-title">
                  {formatDateTime(user.lastAccess)}
                  {user.lastDevice && user.lastDevice !== 'N/A' ? ` · ${user.lastDevice}` : ''}
                </dd>
              </div>
              <div className="col-span-2">
                <dt className="mb-1 font-medium text-text-secondary">Assinatura</dt>
                <dd className={cn('font-medium', assinatura.tone)}>{assinatura.text}</dd>
              </div>
              <div>
                <dt className="mb-1 font-medium text-text-secondary">Animais cadastrados</dt>
                <dd className="font-semibold text-text-title">{user.animalCount ?? 0}</dd>
              </div>
              <div>
                <dt className="mb-1 font-medium text-text-secondary">Funcionários vinculados</dt>
                <dd className="font-semibold text-text-title">
                  {funcionariosCount == null
                    ? '—'
                    : funcionariosCount === 0
                      ? 'nenhum'
                      : funcionariosCount}
                </dd>
              </div>
              {currentDevice && (
                <div>
                  <dt className="mb-1 font-medium text-text-secondary">Versão do app</dt>
                  <dd className="font-semibold text-text-title">
                    {currentDevice.appVersion ?? '—'}
                  </dd>
                </div>
              )}
              {currentDevice && (
                <div className="col-span-2">
                  <dt className="mb-1 font-medium text-text-secondary">Sistema</dt>
                  <dd className="text-text-title">
                    {[currentDevice.platform, currentDevice.osVersion].filter(Boolean).join(' ') ||
                      '—'}
                    {currentDevice.model ? ` · ${currentDevice.model}` : ''}
                  </dd>
                </div>
              )}
              {user.usage && (
                <div>
                  <dt className="mb-1 font-medium text-text-secondary">Dias ativos (30 dias)</dt>
                  <dd className="text-text-title">
                    <span className="font-semibold">{user.usage.daysActive30d}</span> de 30
                  </dd>
                </div>
              )}
              {user.usage && (
                <div>
                  <dt className="mb-1 font-medium text-text-secondary">Erros no app (30 dias)</dt>
                  <dd
                    className={cn(
                      'font-semibold',
                      user.usage.errors30d > 0 ? 'text-error' : 'text-text-title'
                    )}
                  >
                    {user.usage.errors30d === 0 ? 'nenhum' : user.usage.errors30d}
                  </dd>
                </div>
              )}
              {user.account && (
                <div>
                  <dt className="mb-1 font-medium text-text-secondary">Último login</dt>
                  <dd className="text-text-title">{formatDateTime(user.account.lastLogin)}</dd>
                </div>
              )}
              {user.usage?.firstSeen && (
                <div>
                  <dt className="mb-1 font-medium text-text-secondary">Primeiro uso do app</dt>
                  <dd className="text-text-title">{formatDateTime(user.usage.firstSeen)}</dd>
                </div>
              )}
              <div>
                <dt className="mb-1 font-medium text-text-secondary">Criado em</dt>
                <dd className="text-text-title">{formatDateTime(user.createdAt)}</dd>
              </div>
            </dl>
          </aside>

          {/* Direita — propriedade + atividade */}
          <div className="flex-1 space-y-6 p-4 lg:space-y-8 lg:overflow-y-auto lg:p-6">
            {/* Propriedade */}
            <section>
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-text-secondary">
                Propriedade
              </h3>
              {prop ? (
                <div className="rounded-xl border border-border bg-card p-4 shadow-card">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="flex size-10 items-center justify-center rounded-lg bg-tertiary/10 text-tertiary">
                        <Home size={18} />
                      </span>
                      <div>
                        <p className="font-semibold text-text-title">{prop.name || 'Sem nome'}</p>
                        <p className="text-sm text-text-secondary">
                          {prop.city || 'N/A'}
                          {prop.uf ? `, ${prop.uf}` : ''}
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 text-sm font-medium text-text-secondary">
                      {prop.area_total || 0} ha totais
                    </span>
                  </div>

                  {/* Barra de áreas */}
                  <div className="mt-4 flex h-2 w-full overflow-hidden rounded-full bg-muted">
                    {areasSoma > 0 &&
                      areas
                        .filter((a) => a.ha > 0)
                        .map((a) => (
                          <div
                            key={a.label}
                            className={a.bar}
                            style={{ width: `${(a.ha / areasSoma) * 100}%` }}
                            title={`${a.label}: ${a.ha} ha`}
                          />
                        ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-xs text-text-secondary">
                    {areas.map((a) => (
                      <span key={a.label} className="inline-flex items-center gap-1.5">
                        <span className={cn('size-2 rounded-full', a.dot)} />
                        {a.label} {a.ha} ha
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-text-secondary">
                  Sem propriedade cadastrada.
                </div>
              )}

              {otherProperties.length > 0 && (
                <div className="mt-3">
                  <p className="mb-2 text-xs text-text-secondary">Também vinculado a</p>
                  <ul className="divide-y divide-border rounded-xl border border-border bg-card">
                    {otherProperties.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center justify-between gap-3 px-4 py-2.5"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-text-title">{p.name}</p>
                          <p className="truncate text-xs text-text-secondary">
                            {p.city || 'N/A'}
                            {p.uf ? `, ${p.uf}` : ''}
                          </p>
                        </div>
                        <span className="shrink-0 rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium capitalize text-text-secondary">
                          {p.isOwner ? 'dono' : p.vinculo || 'vínculo'}
                          {p.status && p.status !== 'ativo' ? ` · ${p.status}` : ''}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            <UserDevicesSection devices={devices} />

            {user.isProducer && can('finance') && (
              <CobrancaRecorrenteSection userId={Number(user.id)} />
            )}

            {/* Atividade recente (atividade + jornada de navegação, mescladas) */}
            <section>
              <div className="mb-3 flex items-center justify-between gap-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-text-secondary">
                  Atividade recente
                </h3>
                {activity && activity.length > 0 && daysSince(activity[0].at) >= 3 && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-warning/10 px-2.5 py-1 text-xs font-medium text-warning">
                    <AlertTriangle size={13} />
                    Sem atividade há {daysSince(activity[0].at)} dias
                  </span>
                )}
              </div>
              {activityLoading ? (
                <div className="flex items-center gap-2 py-4 text-sm text-text-secondary">
                  <RefreshCw size={16} className="animate-spin" />
                  Carregando…
                </div>
              ) : activity && activity.length > 0 ? (
                <ul className="space-y-1">
                  {activity.map((item, i) => {
                    const meta = ACTIVITY_META[item.type];
                    const Icon = meta.icon;
                    return (
                      <li
                        key={`${item.type}-${item.at}-${i}`}
                        className="flex items-center gap-3 py-2"
                      >
                        <span
                          className={cn(
                            'flex size-8 shrink-0 items-center justify-center rounded-lg',
                            meta.iconBg
                          )}
                        >
                          <Icon size={15} />
                        </span>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium text-text-title">
                            {item.title}
                          </p>
                          {item.subtitle && (
                            <p className="truncate text-xs text-text-secondary">{item.subtitle}</p>
                          )}
                        </div>
                        <span className="shrink-0 text-xs tabular-nums text-text-secondary">
                          {shortDateTime(item.at)}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="py-4 text-sm text-text-secondary">Nenhuma atividade registrada.</p>
              )}
            </section>
          </div>
        </div>

        {/* Footer */}
        {/* No celular os três botões dividem a largura; no desktop, bloquear
            fica à esquerda e o resto à direita. */}
        <div className="grid grid-cols-3 gap-2 border-t border-border bg-bg-elevated px-4 py-3 lg:flex lg:items-center lg:justify-between lg:gap-3 lg:px-6 lg:py-4">
          {!isBlocked ? (
            <Button
              type="button"
              variant="secondary"
              className="border-warning/50 px-2 text-warning hover:bg-warning/10 lg:px-4"
              onClick={onBlock}
            >
              <Ban size={16} />
              Bloquear
            </Button>
          ) : (
            <span className="flex items-center text-sm font-medium text-error">Bloqueado</span>
          )}
          <div className="col-span-2 grid grid-cols-2 gap-2 lg:flex lg:items-center lg:gap-3">
            <Button
              type="button"
              variant="secondary"
              className="px-2 lg:px-4"
              onClick={onManageSubscription}
            >
              <CreditCard size={16} />
              Assinatura
            </Button>
            <Button type="button" className="px-2 lg:px-4" onClick={onEdit}>
              <Pencil size={16} />
              Editar
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
